import React from 'react';
import { Marker, Popup, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';

/**
 * LayerRenderer Component
 * 
 * Renders a single data type layer on the map.
 * Handles different geometry types:
 * - Point markers (with custom icons)
 * - GeoJSON shapes (polygons, lines, etc.)
 * - Mixed geometry collections
 * 
 * Props:
 * - dataTypeConfig: Configuration from dataTypes.js
 * - data: Array of data items to render
 * - onFeatureClick: Callback when feature is clicked
 */
const LayerRenderer = ({ dataTypeConfig, data, onFeatureClick }) => {
  if (!data || data.length === 0) return null;

  /**
   * Create custom icon for markers
   */
  const createCustomIcon = (item) => {
    console.log('🎨 Creating icon for item:', item.structuredData?.category);
    console.log('📦 dataTypeConfig.iconPalette:', dataTypeConfig.iconPalette);
    console.log('📦 dataTypeConfig.mapDisplay:', dataTypeConfig.mapDisplay);
    
    let icon = item.structuredData?.selectedIcon?.icon;
    
    // If no selectedIcon, check for iconPalette and iconSelector
    if (!icon && dataTypeConfig.iconPalette && dataTypeConfig.mapDisplay?.iconSelector) {
      const iconKey = dataTypeConfig.mapDisplay.iconSelector(item);
      console.log('🔑 Icon key from selector:', iconKey);
      icon = dataTypeConfig.iconPalette[iconKey] || dataTypeConfig.iconPalette.default || dataTypeConfig.icon;
      console.log('✅ Icon from palette:', icon);
    } else if (!icon) {
      icon = dataTypeConfig.icon;
      console.log('📍 Using default icon:', icon);
    }

    const iconHtml = renderToStaticMarkup(
      <div
        style={{
          fontSize: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          filter: `drop-shadow(0 2px 4px rgba(0,0,0,0.3))`
        }}
      >
        {icon}
      </div>
    );

    return L.divIcon({
      html: iconHtml,
      className: 'custom-marker-icon',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });
  };

  /**
   * Create popup content for a feature
   */
  const createPopupContent = (item) => {
    // Use custom popup template if defined
    if (dataTypeConfig.mapConfig?.popupTemplate) {
      return dataTypeConfig.mapConfig.popupTemplate(item);
    }

    // Default popup content
    const iconLabel = item.structuredData?.selectedIcon?.label;
    const title = item.title || item.structuredData?.title || iconLabel || dataTypeConfig.name;
    
    return `
      <div style="min-width: 200px;">
        <strong>${title}</strong><br/>
        ${item.description ? item.description.substring(0, 150) + (item.description.length > 150 ? '...' : '') : ''}
      </div>
    `;
  };

  /**
   * Parse location data (point coordinates)
   */
  const parseLocation = (item) => {
    try {
      if (item.location) {
        // Location is stored as [lat, lng]
        if (Array.isArray(item.location)) {
          return item.location;
        }
        // Location might be stringified
        const parsed = typeof item.location === 'string' ? JSON.parse(item.location) : item.location;
        if (parsed.coordinates) {
          // GeoJSON Point format
          return [parsed.coordinates[1], parsed.coordinates[0]]; // [lat, lng]
        }
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (err) {
      console.error('Error parsing location:', err);
    }
    return null;
  };

  /**
   * Parse GeoJSON data (shapes, polygons, etc.)
   */
  const parseGeoJson = (item) => {
    try {
      if (item.mapData) {
        const parsed = typeof item.mapData === 'string' ? JSON.parse(item.mapData) : item.mapData;
        
        // Check if it's valid GeoJSON
        if (parsed && parsed.type && (parsed.type === 'FeatureCollection' || parsed.type === 'Feature' || parsed.geometry)) {
          return parsed;
        }
      }
    } catch (err) {
      console.error('Error parsing GeoJSON:', err);
    }
    return null;
  };

  /**
   * Get GeoJSON style based on data type and item properties
   */
  const getGeoJsonStyle = (feature) => {
    const baseColor = dataTypeConfig.color || '#4A90E2';
    
    // Check for status-based coloring
    const status = feature.properties?.status?.toLowerCase();
    let color = baseColor;

    if (status === 'active') color = '#28a745';
    else if (status === 'completed') color = '#007bff';
    else if (status === 'draft') color = '#ffc107';
    else if (status === 'cancelled') color = '#dc3545';

    // Check for severity-based coloring (for hazards)
    const severity = feature.properties?.severity?.toLowerCase();
    if (severity?.includes('critical')) color = '#dc3545';
    else if (severity?.includes('high')) color = '#ff6347';
    else if (severity?.includes('medium')) color = '#ffa500';
    else if (severity?.includes('low')) color = '#ffc107';

    return {
      color: color,
      weight: 3,
      opacity: 0.8,
      fillColor: color,
      fillOpacity: 0.2
    };
  };

  /**
   * Handle click on GeoJSON feature
   */
  const onEachFeature = (feature, layer) => {
    const item = feature.properties?.originalData;
    
    if (item) {
      const popupContent = createPopupContent(item);
      layer.bindPopup(popupContent);

      if (onFeatureClick) {
        layer.on('click', () => {
          onFeatureClick(item);
        });
      }
    }
  };

  /**
   * Render point markers
   */
  const renderPointMarkers = () => {
    return data
      .filter(item => parseLocation(item) !== null)
      .map((item, index) => {
        const location = parseLocation(item);
        if (!location) return null;

        const icon = createCustomIcon(item);
        const popupContent = createPopupContent(item);

        return (
          <Marker
            key={`marker-${item._id || index}`}
            position={location}
            icon={icon}
            eventHandlers={{
              click: () => {
                if (onFeatureClick) {
                  onFeatureClick(item);
                }
              }
            }}
          >
            <Popup>
              <div dangerouslySetInnerHTML={{ __html: popupContent }} />
            </Popup>
          </Marker>
        );
      });
  };

  /**
   * Render GeoJSON shapes
   */
  const renderGeoJsonShapes = () => {
    return data
      .filter(item => parseGeoJson(item) !== null)
      .map((item, index) => {
        const geoJsonData = parseGeoJson(item);
        if (!geoJsonData) return null;

        // Add original item data to feature properties for popup
        const enhancedGeoJson = {
          ...geoJsonData,
          properties: {
            ...geoJsonData.properties,
            originalData: item
          }
        };

        // If it's a FeatureCollection, enhance each feature
        if (enhancedGeoJson.type === 'FeatureCollection') {
          enhancedGeoJson.features = enhancedGeoJson.features.map(feature => ({
            ...feature,
            properties: {
              ...feature.properties,
              originalData: item
            }
          }));
        }

        return (
          <GeoJSON
            key={`geojson-${item._id || index}`}
            data={enhancedGeoJson}
            style={getGeoJsonStyle}
            onEachFeature={onEachFeature}
          />
        );
      });
  };

  return (
    <>
      {renderPointMarkers()}
      {renderGeoJsonShapes()}
    </>
  );
};

export default LayerRenderer;
